'use strict';

const errorMessages = require('./lib/error-messages.json');
const helpers = require('./lib/helpers');

/** @module validate-redirects */

/* eslint-disable sonarjs/cognitive-complexity */
/**
 * Validate redirect URIs
 *
 * Smartcar-hosted format:
 * https://javascript-sdk.smartcar.com/v{majorVersion}/redirect?app_origin={valid-HTTPS-or-localhost-HTTP-URI}
 *
 * Deprecated legacy format:
 * https://javascript-sdk.smartcar.com/redirect-{version}?app_origin={valid-HTTPS-or-localhost-HTTP-URI}
 *
 * The path must start with `/v{majorVersion}/redirect` or `/redirect` (deprecated).
 * The `app_origin` parameter must be a valid HTTPS URI or
 * `http://localhost` (optional port) w/o path, query params or hashes.
 *
 * Note: when the Node URL library parses query params from a URL it parses
 * hashes separately from the query parameters. Therefore an `app_origin` of
 * `https://myapp.com#shouldnt-have-hashes` will be parsed to
 * `https://myapp.com` and `#shouldnt-have-hashes` will be treated as a hash on
 * `https://javascript-sdk.smartcar.com`.
 *
 *
 * @throws descriptive error if redirect URI is invalid
 * @param {string} _url - redirect URI to validate
 * @param {string} [clientId=''] - clientId of the application
 * @returns {URL} parsed URL object if valid
 */
module.exports = function validate(_url, clientId = '') {
  if (_url.length === 0) {
    throw new Error(errorMessages.zeroLength);
  }

  const url = helpers.validateURL(_url);

  switch (url.protocol) {
    case 'http:':
      if (!helpers.isLocalhost(url)) {
        throw new Error(errorMessages.invalidHTTP);
      }
      break;
    case 'https:':
      if (!helpers.isHTTPS(url) && !helpers.isLocalhost(url)) {
        throw new Error(errorMessages.invalidHTTPS);
      }

      if (url.href.startsWith('https://javascript-sdk.smartcar.com')) {
        if (!helpers.hasValidPath(url)) {
          throw new Error(errorMessages.invalidPath);
        }

        // allow only `app_origin` query parameter
        helpers.validateQueryParams(url);

        const appOriginUrl = helpers.validateURL(
          url.searchParams.get('app_origin'),
          errorMessages.malformedAppOrigin,
        );

        if (
          !(helpers.isHTTPS(appOriginUrl) || helpers.isLocalhost(appOriginUrl))
        ) {
          throw new Error(errorMessages.invalidAppOriginProtocol);
        }

        if (appOriginUrl.pathname !== '/') {
          throw new Error(errorMessages.unexpectedAppOriginPath);
        }

        if (appOriginUrl.search !== '') {
          throw new Error(errorMessages.unexpectedAppOriginQueryParam);
        }

        // hash parsed separately from `app_origin` validated on original
        if (url.hash !== '') {
          throw new Error(errorMessages.unexpectedAppOriginHash);
        }
      }
      break;
    default:
      // check for custom scheme
      // difference from `http` & `https`, confusion around custom scheme could
      // make it more likely for user's to enter protocol wrong, for `http` &
      // `https` cases throwing an error saying invalidProtocol should be enough
      helpers.validateCustomScheme(url, clientId);
  }

  return url;
};
/* eslint-enable */
